import { WarningIcon } from '@plato/ui-legacy/assets/icons/warning-icon';
import { BaseConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';

export type DeleteFilterViewDialogProps = {
    viewId: string;
    viewKey: string;
    onDeleted?: (viewId: string) => void;
} & ModalInnerProps;

export const DeleteFilterViewDialog = DialogStateManager.create<DeleteFilterViewDialogProps>(
    ({ viewId, viewKey, onDeleted }: DeleteFilterViewDialogProps) => {
        const { t } = useTranslation('filters');
        const { t: tCommon } = useTranslation('common');
        const [isDeleting, setIsDeleting] = useState(false);
        const modal = useDialog();
        const utils = api.useUtils();
        const sendEvent = useAnalytics();

        const deleteMutation = api.platoUser.command.filters.delete.useMutation({
            onSuccess: () => {
                void utils.platoUser.query.filters.getMany.invalidate({ viewKey });
                onDeleted?.(viewId);
                modal.remove();
                sendEvent('filter_view_deleted', { view_id: viewId, view_key: viewKey });
            },
            onSettled: () => {
                setIsDeleting(false);
            },
        });

        const handleSubmit = async () => {
            setIsDeleting(true);
            deleteMutation.mutate({ id: viewId });
        };

        const handleClose = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('saved_views__delete_confirmation_title')}
                description={t('saved_views__delete_confirmation_description')}
                icon={WarningIcon}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleClose}>
                        {tCommon('form__cancel')}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleSubmit} isLoading={isDeleting}>
                        {tCommon('form__delete')}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
